/**
 * Zentrale API-Konfiguration für den MediaBox Client
 * 
 * Dieser File definiert alle API-Endpunkte und konfiguriert die Verbindung zum NestJS-Server
 * Basierend auf OpenAPI 3.0 Spezifikation
 */

// API Base URL aus Environment Variable
// Production ohne gesetzte Variable nutzt bewusst same-origin relative Pfade.
const configuredApiBaseUrl = process.env.REACT_APP_API_ENDPOINT;

function resolveApiBaseUrl() {
  if (configuredApiBaseUrl !== undefined) {
    return configuredApiBaseUrl;
  }

  if (process.env.NODE_ENV === 'production') {
    return '';
  }

  return 'http://localhost:3001';
}

export const API_BASE_URL = resolveApiBaseUrl();

// Legacy CELUM API (falls noch benötigt)
export const CELUM_ENDPOINT = process.env.REACT_APP_CELUM_ENDPOINT || 'https://dam.celum.net/cora';

/**
 * API-Endpunkte für NestJS Backend
 */
export const API_ENDPOINTS = {
  // Auth Endpunkte
  AUTH: {
    LOGIN: `${API_BASE_URL}/auth/login`,
    LOGOUT: `${API_BASE_URL}/auth/logout`,
    SIGNUP: `${API_BASE_URL}/auth/signup`,
    REFRESH_TOKEN: `${API_BASE_URL}/auth/refresh-token`,
    VERIFY_EMAIL: (token) => `${API_BASE_URL}/auth/email/verify/${token}`,
    RESEND_VERIFICATION: (email) => `${API_BASE_URL}/auth/email/resend-verification/${email}`,
    FORGOT_PASSWORD: `${API_BASE_URL}/auth/email/forgot-password`,
    RESET_PASSWORD: `${API_BASE_URL}/auth/email/reset-password`,
    ACCEPT_USER: `${API_BASE_URL}/auth/accept-user`,
    SSO: {
      LOGIN: `${API_BASE_URL}/auth/sso/login`,
      CALLBACK: `${API_BASE_URL}/auth/sso`,
      METADATA: `${API_BASE_URL}/auth/sso/metadata`,
    },
  },

  // User Endpunkte
  USER: {
    ME: `${API_BASE_URL}/users/me`,
    ALL: `${API_BASE_URL}/users`,
    BY_ID: (userId) => `${API_BASE_URL}/users/${userId}`,
    UPDATE: (userId) => `${API_BASE_URL}/users/${userId}`,
    DELETE: (userId) => `${API_BASE_URL}/users/${userId}`,
    GROUPS: (includeUsers = false) => `${API_BASE_URL}/users/groups${includeUsers ? '?includeUsers=true' : ''}`,
    CREATE_GROUP: `${API_BASE_URL}/users/groups`,
  },

  // Node Endpunkte
  NODES: {
    ALL: `${API_BASE_URL}/nodes`,
    BY_ID: (nodeId) => `${API_BASE_URL}/nodes/${nodeId}`,
    CREATE: `${API_BASE_URL}/nodes`,
    UPDATE: (nodeId) => `${API_BASE_URL}/nodes/${nodeId}`,
    DELETE: (nodeId) => `${API_BASE_URL}/nodes/${nodeId}`,
    LATEST: (nodeId) => `${API_BASE_URL}/nodes/${nodeId}/latest`,
    PERMISSIONS: (nodeId) => `${API_BASE_URL}/nodes/${nodeId}/permissions`,
    UPLOAD_HEADER: `${API_BASE_URL}/nodes/upload`,
    HEADER_IMAGE: (nodeId) => `${API_BASE_URL}/nodes/${nodeId}/header-image`,
  },

  // Asset Endpunkte
  ASSETS: {
    ALL: `${API_BASE_URL}/assets`,
    BY_ID: (assetId) => `${API_BASE_URL}/assets/${assetId}`,
    CREATE: `${API_BASE_URL}/assets`,
    UPDATE: (assetId) => `${API_BASE_URL}/assets/${assetId}`,
    DELETE: (assetId) => `${API_BASE_URL}/assets/${assetId}`,
    ARCHIVE: (assetId) => `${API_BASE_URL}/assets/${assetId}/archive`,
    PREVIEW: (assetId) => `${API_BASE_URL}/assets/preview/${assetId}`,
    DOWNLOAD: (assetId, formatId) => `${API_BASE_URL}/assets/download/${assetId}/${formatId}`,
    SEARCH: `${API_BASE_URL}/assets/search`,
    UPLOAD: `${API_BASE_URL}/assets/upload`,
    CHECK_DUPLICATES: `${API_BASE_URL}/assets/check-duplicates`,
    IMAGE_PROCESSING_STATUS: (jobId) => `${API_BASE_URL}/assets/image-processing-status/${jobId}`,
    ASSET_PROCESSING_STATUS: (assetId) => `${API_BASE_URL}/assets/${assetId}/image-processing-status`,
  },

  // Cart Endpunkte
  CART: {
    GET: `${API_BASE_URL}/cart`,
    ADD: `${API_BASE_URL}/cart/add`,
    REMOVE: `${API_BASE_URL}/cart`,
    CLEAR: `${API_BASE_URL}/cart/clear`,
    DOWNLOAD: `${API_BASE_URL}/cart/download`,
    DOWNLOAD_OUTGOING: (id) => `${API_BASE_URL}/cart/download/${id}`,
    LIST: `${API_BASE_URL}/cart/list`,
    SEND: `${API_BASE_URL}/cart/send`,
  },

  // File Endpunkte
  FILE: {
    BY_ID: (fileId, imageSize) => `${API_BASE_URL}/file/${fileId}/${imageSize}`,
    DOWNLOAD: (assetId, downloadformatId) => `${API_BASE_URL}/assets/download/${assetId}/${downloadformatId}`,
  },

  // Name Endpunkte
  NAME: {
    BY_ID: (id) => `${API_BASE_URL}/name/${id}`,
    UPDATE: (id) => `${API_BASE_URL}/name/${id}`,
  },

  // Notifications
  NOTIFICATIONS: {
    ALL: `${API_BASE_URL}/notifications`,
    READ: (notificationId) => `${API_BASE_URL}/notifications/read/${notificationId}`,
    DELETE: (notificationId) => `${API_BASE_URL}/notifications/${notificationId}`,
  },

  // Metadata
  METADATA: {
    ALL: `${API_BASE_URL}/metadata`,
    BY_ID: (id) => `${API_BASE_URL}/metadata/${id}`,
    CREATE: `${API_BASE_URL}/metadata`,
    UPDATE: (id) => `${API_BASE_URL}/metadata/${id}`,
    ITEMS: `${API_BASE_URL}/metadata`,
    ITEMS_BY_ID: (id) => `${API_BASE_URL}/metadata/items/${id}`,
    ITEMS_CREATE: `${API_BASE_URL}/metadata/items`,
    ITEMS_UPDATE: (id) => `${API_BASE_URL}/metadata/items/${id}`,
    ITEMS_DELETE: (id) => `${API_BASE_URL}/metadata/items/${id}`,
  },

  // Translation
  TRANSLATION: {
    ALL: `${API_BASE_URL}/translation`,
    BY_ID: (id) => `${API_BASE_URL}/translation/${id}`,
    CREATE: `${API_BASE_URL}/translation`,
    UPDATE: (id) => `${API_BASE_URL}/translation/${id}`,
    DELETE: (id) => `${API_BASE_URL}/translation/${id}`,
    ALL_FOR_LANG: (lang) => `${API_BASE_URL}/translation/all/${lang}`,
    AVAILABLE_LANGUAGES: `${API_BASE_URL}/translation/available`,
  },

  // Download Formats
  DOWNLOADFORMAT: {
    ALL: `${API_BASE_URL}/downloadformat`,
    BY_ID: (id) => `${API_BASE_URL}/downloadformat/${id}`,
    CREATE: `${API_BASE_URL}/downloadformat`,
    UPDATE: (id) => `${API_BASE_URL}/downloadformat/${id}`,
    DELETE: (id) => `${API_BASE_URL}/downloadformat/${id}`,
  },

  // Asset Types
  ASSETTYPE: {
    ALL: `${API_BASE_URL}/assettype`,
    BY_ID: (id) => `${API_BASE_URL}/assettype/${id}`,
    CREATE: `${API_BASE_URL}/assettype`,
    UPDATE: (id) => `${API_BASE_URL}/assettype/${id}`,
    DELETE: (id) => `${API_BASE_URL}/assettype/${id}`,
  },

  // Regions
  REGION: {
    ALL: `${API_BASE_URL}/region`,
    BY_ID: (id) => `${API_BASE_URL}/region/${id}`,
    CREATE: `${API_BASE_URL}/region`,
    UPDATE: (id) => `${API_BASE_URL}/region/${id}`,
    DELETE: (id) => `${API_BASE_URL}/region/${id}`,
  },

  // Product Imaging Orders (neu)
  PRODUCT_IMAGING_ORDERS: {
    ALL: `${API_BASE_URL}/product-imaging-orders`,
    BY_ID: (id) => `${API_BASE_URL}/product-imaging-orders/${id}`,
    CREATE: `${API_BASE_URL}/product-imaging-orders`,
    UPDATE_STATUS: (id) => `${API_BASE_URL}/product-imaging-orders/${id}/status`,
    UPLOAD: (id) => `${API_BASE_URL}/product-imaging-orders/${id}/upload`,
    ADD_COMMENT: (id) => `${API_BASE_URL}/product-imaging-orders/${id}/comments`,
    UPDATE_COMMENT: (commentId) => `${API_BASE_URL}/product-imaging-orders/comments/${commentId}`,
    DELETE_COMMENT: (commentId) => `${API_BASE_URL}/product-imaging-orders/comments/${commentId}`,
    APPROVE: (id) => `${API_BASE_URL}/product-imaging-orders/${id}/approve`,
    REQUEST_CHANGES: (id) => `${API_BASE_URL}/product-imaging-orders/${id}/request-changes`,
    ADMIN_ALL: `${API_BASE_URL}/product-imaging-orders/admin/all`,
    FILE: (fileId, size) => `${API_BASE_URL}/product-imaging-orders/files/${fileId}/${size}`,
    DELETE_FILE: (fileId) => `${API_BASE_URL}/product-imaging-orders/files/${fileId}`,
    REPLACE_FILE: (fileId) => `${API_BASE_URL}/product-imaging-orders/files/${fileId}/replace`,
    CREATE_ASSET: (id) => `${API_BASE_URL}/product-imaging-orders/${id}/create-asset`,
  },

  // Action Forms (neu)
  ACTION_FORMS: {
    ALL: `${API_BASE_URL}/action-forms`,
    BY_ID: (id) => `${API_BASE_URL}/action-forms/${id}`,
    CREATE: `${API_BASE_URL}/action-forms`,
    UPDATE: (id) => `${API_BASE_URL}/action-forms/${id}`,
    DELETE: (id) => `${API_BASE_URL}/action-forms/${id}`,
    UPDATE_STATUS: (id, status) => `${API_BASE_URL}/action-forms/${id}/status/${status}`,
  },

  // Admin
  ADMIN: {
    ALL: `${API_BASE_URL}/admin`,
    USER_GROUPS: `${API_BASE_URL}/admin/user-groups`,
    SEND_TEST_MAIL: `${API_BASE_URL}/admin/send-test-mail`,
    UNCONFIRMED_USERS: `${API_BASE_URL}/admin/unconfirmed-users`,
    RESEND_CONFIRMATION: (userId) => `${API_BASE_URL}/admin/resend-confirmation/${userId}`,
    ASSET_STATS: `${API_BASE_URL}/admin/asset-stats`,
    STATS_ASSETS: (from, to) => `${API_BASE_URL}/admin/stats/assets?from=${from}&to=${to}`,
    STATS_DOWNLOADS: (from, to) => `${API_BASE_URL}/admin/stats/downloads?from=${from}&to=${to}`,
    STATS_NEW_USERS: (from, to) => `${API_BASE_URL}/admin/stats/new-users?from=${from}&to=${to}`,
  },

  // Health Check
  HEALTH: {
    STATUS: `${API_BASE_URL}/health`,
    DB: `${API_BASE_URL}/health/db`,
    SSO_USER: (email) => `${API_BASE_URL}/health/sso-user/${email}`,
    SSO_NODES: (userId) => `${API_BASE_URL}/health/sso-nodes/${userId}`,
    PERMISSIONS_INTEGRITY: `${API_BASE_URL}/health/permissions-integrity`,
  },
};

/**
 * Hilfsfunktion zum Erstellen von Authorization Headers
 */
export const getAuthHeaders = () => {
  const user = JSON.parse(localStorage.getItem('user'));
  return {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'Authorization': user && user.token ? `Bearer ${user.token}` : '',
  };
};

/**
 * Basis HTTP Headers
 */
export const getBaseHeaders = () => ({
  'Accept': 'application/json',
  'Content-Type': 'application/json',
});

export default {
  API_BASE_URL,
  API_ENDPOINTS,
  getAuthHeaders,
  getBaseHeaders,
};
